import randomColor from 'randomcolor';
import chroma from 'chroma-js'; // Excellent color manipulation library
import * as d3 from 'd3';


export function sampleModernColors(count) {
  const colors = new Set();

  // Define the color scale using a cool-blue/green/purple scheme
  const colorScale = d3.scaleSequential(d3.interpolateCool).domain([0, 1]);

  while (colors.size < count) {
    // Generate a random value within the domain
    const t = Math.random() * (colorScale.domain()[1] - colorScale.domain()[0]) + colorScale.domain()[0];

    // Retrieve the color from the scale and convert to hexadecimal
    const color = d3.color(colorScale(t)).formatHex();

    // Check for color proximity
    let isTooClose = false;
    colors.forEach(c => {
      if (d3.color(c).formatHex() === color) {
        isTooClose = true;
      }
    });

    if (!isTooClose) {
      colors.add(color);
    }
  }

  return Array.from(colors);
}

export const sampleColors = (nColors, hue = null, luminosity, format = 'hex', opacity = 1) => {
  const colors = randomColor({
    count: nColors,
    hue: !hue || Array.isArray(hue) && hue.length > 1 ? 'random' : hue,
    luminosity: luminosity, // or a value between 0-50
    alpha: opacity,
    format: format
  });

  if (format === 'rgba' && opacity !== 1) {
    return colors.map((color) => {
      const c = chroma(color);
      return c.alpha(opacity).toString(); // Adjust alpha value
    });
  }

  return colors;
};


const randomInRange = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;

// Define color ranges in terms of hue (in degrees), allowing for specific aesthetic shades
const colorRanges = [
  { min: 160, max: 240 }, // Blues
  { min: 130, max: 190 }, // Cyan to Teal
  { min: 60, max: 180 },  // Green to Yellow-Green
  { min: 200, max: 280 }, // Blue to Purple
  { min: 300, max: 360 }  // Dark Crimson to Deep Scarlet Red
];

export const sampleChromaColors = (nColors, format = 'hex') => {
  const colors = [];

  // Pre-calculate color steps within each range to ensure even distribution
  const colorSteps = colorRanges.map(range =>
    Math.floor((range.max - range.min) / nColors)
  );

  for (let i = 0; i < nColors; i++) {
    let color;
    let isTooClose = false;

    do {
      isTooClose = false;
      const rangeIndex = Math.floor(Math.random() * colorRanges.length);
      const range = colorRanges[rangeIndex];

      // Calculate hue with stepping to avoid clustering
      const hue = range.min + (colorSteps[rangeIndex] * i) +
                  (Math.random() * colorSteps[rangeIndex]);

      color = chroma.hsl(hue, 0.5, 0.3);

      // Check for color similarity
      for (let j = 0; j < colors.length; j++) {
        if (chroma.distance(color, colors[j]) < 0.2) { // Adjust threshold as needed
          isTooClose = true;
          break;
        }
      }
    } while (isTooClose);

    colors.push(color);
  }

  return colors.map(color => format === 'hsl' ? color.hsl() : color.hex());
};
export function _generateSequential(scale, count) {
  const colors = new Set();
  while (colors.size < count) {
    const t = Math.random() * (scale.domain()[1] - scale.domain()[0]) + scale.domain()[0];
    const color = d3.color(scale(t)).formatHex();
    colors.add(color);
  }
  return Array.from(colors);
}

export function createSequentialBlues(n) {
  const colorScale = d3.scaleSequential(d3.interpolateBlues).domain([0, 1]);
  return _generateSequential(colorScale, n);
}

export function createSequentialReds(n) {
  const colorScale = d3.scaleSequential(d3.interpolateReds).domain([0, 1]);
  return _generateSequential(colorScale, n);
}

export function createSequentialGreens(n) {
  const colorScale = d3.scaleSequential(d3.interpolateGreens).domain([0, 1]);
  return _generateSequential(colorScale, n);
}

export function createSequentialPurples(n) {
  const colorScale = d3.scaleSequential(d3.interpolatePurples).domain([0, 1]);
  return _generateSequential(colorScale, n);
}